import { useState, useMemo } from "react";
import api, { errText } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { RationGroupsEditor } from "./RationGroupsEditor";
import { Loader2, AlertTriangle, CheckCircle2, ChevronDown, Search, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { todayISO, groupsToLines, groupRationBySlot, fmtQty, isFullyOwnerManaged } from "../lib/feedingConstants";

const STATUS_LABEL = {
  created: "Nuova profenda creata", added: "Alimento aggiunto", modified: "Quantità modificata",
  replaced: "Profenda sostituita", noop: "Nessuna modifica", excluded: "Esclusa · gestita dal proprietario", error: "Errore",
};
const CountRow = ({ testid, label, value, danger }) => (
  <div className="flex items-center justify-between py-1 text-sm" data-testid={testid}>
    <span className={danger && value > 0 ? "text-red-600" : "text-[#5F6B64]"}>{label}</span>
    <span className={`font-semibold ${danger && value > 0 ? "text-red-600" : "text-[#1B3B2B]"}`}>{value}</span>
  </div>
);

// Bulk actions on selected horses' rations. STEP 1 selects the horses from the FULL stable
// population (independent from the main Feeding list/filters/visibility — a horse with no
// profenda must be reachable here). STEP 2 picks the action; STEP 3 is the mandatory dry-run.
// Fully owner-managed horses can't be picked (stable doesn't manage their feeding). Each modified
// horse gets a new independent version — no linked/group rations. Backend semantics unchanged.
export function FeedingBulkDialog({ open, stableId, horses, settingByHorse, activeSlots, activeItems, itemsMap, catOrder, onClose, onApplied }) {
  const [step, setStep] = useState("select");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [q, setQ] = useState("");

  const [mode, setMode] = useState("add_update");
  const [replaceSource, setReplaceSource] = useState("build");
  const [groups, setGroups] = useState([]);
  const [copyFrom, setCopyFrom] = useState("");
  const [copyRation, setCopyRation] = useState(undefined);
  const [validFrom, setValidFrom] = useState(todayISO());
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const ownerFull = (h) => isFullyOwnerManaged((settingByHorse || {})[h.id]);
  const horseIds = useMemo(() => [...selectedIds], [selectedIds]);
  const selectedHorses = useMemo(() => horses.filter((h) => selectedIds.has(h.id)), [horses, selectedIds]);
  const n = selectedIds.size;

  const searched = useMemo(() => {
    const t = q.trim().toLowerCase();
    return horses.filter((h) => !t || (h.display_name || "").toLowerCase().includes(t));
  }, [horses, q]);
  const selectableSearched = searched.filter((h) => !ownerFull(h));

  const toggle = (hid) => setSelectedIds((s) => { const x = new Set(s); x.has(hid) ? x.delete(hid) : x.add(hid); return x; });
  const selectAllFiltered = () => setSelectedIds((s) => { const x = new Set(s); selectableSearched.forEach((h) => x.add(h.id)); return x; });
  const deselectAll = () => setSelectedIds(new Set());

  const invalidate = () => { setPreview(null); setResult(null); };
  const patchGroups = (g) => { setGroups(g); invalidate(); };

  const loadCopySource = (hid) => {
    setCopyFrom(hid); setCopyRation(undefined); invalidate();
    if (!hid) return;
    api.get(`/stables/${stableId}/horses/${hid}/feed-ration`).then((r) => setCopyRation(r.data || null)).catch(() => setCopyRation(null));
  };

  const buildPayload = (dry_run) => {
    const base = { horse_ids: horseIds, valid_from: validFrom, dry_run, mode };
    if (mode === "add_update") {
      const { lines, error } = groupsToLines(groups);
      if (error) { toast.error(error); return null; }
      if (!lines.length) { toast.error("Aggiungi almeno un alimento."); return null; }
      return { ...base, entries: lines };
    }
    if (replaceSource === "copy") {
      if (!copyFrom) { toast.error("Seleziona un cavallo sorgente."); return null; }
      return { ...base, source_horse_id: copyFrom };
    }
    const { lines, error } = groupsToLines(groups);
    if (error) { toast.error(error); return null; }
    if (!lines.length) { toast.error("Costruisci la profenda da applicare."); return null; }
    return { ...base, lines };
  };

  const call = async (dry_run) => {
    const payload = buildPayload(dry_run); if (!payload) return null;
    setBusy(true);
    try {
      const r = await api.post(`/stables/${stableId}/feed-rations/bulk`, payload);
      return r.data;
    } catch (e) { toast.error(errText(e.response?.data?.detail)); return null; }
    finally { setBusy(false); }
  };
  const runPreview = async () => { const d = await call(true); if (d) { setPreview(d); setResult(null); } };
  const runApply = async () => { const d = await call(false); if (d) { setResult(d); onApplied && onApplied(); } };

  const copyGroups = copyRation ? groupRationBySlot(copyRation.lines, activeSlots.filter((s) => !s.archived), itemsMap, catOrder) : [];

  const [openStatus, setOpenStatus] = useState({});
  const toggleStatus = (k) => setOpenStatus((s) => ({ ...s, [k]: !s[k] }));
  const foodName = (id) => itemsMap[id]?.name || "?";
  const foodUnit = (id) => itemsMap[id]?.base_unit || "";
  const slotName = (id) => activeSlots.find((s) => s.id === id)?.name || "";
  const changeText = (c) => {
    const label = `${slotName(c.meal_slot_id)} · ${foodName(c.feed_item_id)}`;
    const u = foodUnit(c.feed_item_id);
    const to = fmtQty(c.to, u);
    return c.added ? `${label}: → ${to}` : `${label}: ${c.from === null || c.from === undefined ? "nessun dato" : fmtQty(c.from, u)} → ${to}`;
  };

  const ImpactSections = ({ data, testidPrefix }) => {
    const byStatus = {};
    (data.results || []).forEach((r) => { (byStatus[r.status] = byStatus[r.status] || []).push(r); });
    const order = ["created", "added", "modified", "replaced", "noop", "excluded", "error"];
    return (
      <div className="divide-y divide-[#F0EAE1]">
        {order.filter((k) => (data.counts?.[k] || 0) > 0).map((k) => {
          const list = byStatus[k] || [];
          const isOpen = !!openStatus[k];
          return (
            <div key={k} data-testid={`${testidPrefix}-section-${k}`}>
              <button type="button" data-testid={`${testidPrefix}-toggle-${k}`} onClick={() => toggleStatus(k)} className="w-full flex items-center justify-between py-1.5 text-sm">
                <span className="flex items-center gap-1.5"><ChevronDown className={`h-3.5 w-3.5 text-[#8C532B] transition-transform ${isOpen ? "" : "-rotate-90"}`} /><span className={k === "error" ? "text-red-600" : "text-[#5F6B64]"}>{STATUS_LABEL[k]}</span></span>
                <span className={`font-semibold ${k === "error" ? "text-red-600" : "text-[#1B3B2B]"}`}>{data.counts[k]}</span>
              </button>
              {isOpen && (
                <div className="pb-2 pl-5 max-h-48 overflow-y-auto space-y-1" data-testid={`${testidPrefix}-list-${k}`}>
                  {list.length === 0 ? <p className="text-xs text-[#9AA39C]">—</p> : list.map((r) => (
                    <div key={r.horse_id} className="text-xs leading-tight">
                      <span className="font-medium text-[#1B3B2B]">{r.horse_name}</span>
                      {k === "error" && r.message && <span className="text-red-600"> — {r.message}</span>}
                      {r.changes?.length > 0 && <span className="text-[#8C532B]"> — {r.changes.map(changeText).join("; ")}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="bulk-dialog">
        <DialogHeader><DialogTitle className="font-display">{step === "select" ? "Azione multipla · seleziona cavalli" : `Azione multipla · ${n} cavall${n === 1 ? "o" : "i"}`}</DialogTitle></DialogHeader>

        {step === "select" ? (
          <div className="space-y-3" data-testid="bulk-select-step">
            <p className="text-xs text-[#8C532B]">Seleziona i cavalli su cui operare. Include anche i cavalli senza profenda o non mostrati nella lista principale.</p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
              <input data-testid="bulk-horse-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cavallo…" className="w-full pl-9 pr-3 py-2 rounded-md bg-white border border-[#E5DEC9] text-base sm:text-sm outline-none placeholder:text-[#9aa39c]" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-[#1B3B2B]" data-testid="bulk-selected-count">{n} selezionat{n === 1 ? "o" : "i"}</span>
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" size="sm" data-testid="bulk-select-all" onClick={selectAllFiltered}>Seleziona tutti</Button>
                <Button variant="ghost" size="sm" data-testid="bulk-deselect-all" onClick={deselectAll}>Deseleziona</Button>
              </div>
            </div>
            <div className="rounded-lg border border-[#E5DEC9] bg-white divide-y divide-[#F0EAE1] max-h-[46vh] overflow-y-auto" data-testid="bulk-horse-list">
              {searched.length === 0 ? <p className="py-8 text-center text-sm text-[#9AA39C]">Nessun cavallo.</p>
                : searched.map((h) => {
                  const disabled = ownerFull(h);
                  const on = selectedIds.has(h.id);
                  return (
                    <button key={h.id} type="button" data-testid={`bulk-horse-${h.id}`} disabled={disabled} onClick={() => toggle(h.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : on ? "bg-[#F7F3EC]" : "hover:bg-[#FBF8F2]"}`}>
                      <span className={`shrink-0 h-5 w-5 rounded border flex items-center justify-center ${on ? "bg-[#1B3B2B] border-[#1B3B2B]" : "bg-white border-[#C9BDA0]"}`}>{on && <Check className="h-3.5 w-3.5 text-white" />}</span>
                      <span className="font-semibold text-[#1B3B2B] truncate flex-1 min-w-0">{h.display_name}</span>
                      {disabled && <span data-testid={`bulk-horse-owner-${h.id}`} className="text-[11px] text-[#8C6D4F] italic shrink-0">Gestita dal proprietario</span>}
                    </button>
                  );
                })}
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" data-testid="bulk-select-cancel" onClick={onClose}>Annulla</Button>
              <Button type="button" data-testid="bulk-select-continue" disabled={n === 0} onClick={() => setStep("action")} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Continua</Button>
            </DialogFooter>
          </div>
        ) : result ? (
          <div className="space-y-4" data-testid="bulk-result">
            <div className="flex items-center gap-2 text-[#1B3B2B]"><CheckCircle2 className="h-5 w-5 text-[#1B3B2B]" /><p className="font-semibold">Operazione completata</p></div>
            <div className="rounded-lg border border-[#E5DEC9] bg-white p-3">
              <CountRow testid="bulk-result-total" label="Selezionati" value={result.total} />
              <ImpactSections data={result} testidPrefix="bulk-result" />
            </div>
            <DialogFooter><Button data-testid="bulk-close" onClick={onClose} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Chiudi</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <button type="button" data-testid="bulk-back-to-select" onClick={() => { setStep("select"); invalidate(); }} className="flex items-center gap-1 text-sm text-[#5F6B64]"><ArrowLeft className="h-4 w-4" /> Cambia selezione ({n})</button>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="bulk-mode-add" onClick={() => { setMode("add_update"); invalidate(); }} className={`rounded-lg border p-3 text-left transition-colors ${mode === "add_update" ? "border-[#1B3B2B] bg-[#F7F3EC]" : "border-[#E5DEC9] bg-white"}`}>
                <p className="font-semibold text-sm text-[#1B3B2B]">Aggiungi / Modifica alimento</p>
                <p className="text-xs text-[#5F6B64] mt-0.5">Aggiunge o aggiorna voci, preservando il resto della profenda.</p>
              </button>
              <button type="button" data-testid="bulk-mode-replace" onClick={() => { setMode("replace"); invalidate(); }} className={`rounded-lg border p-3 text-left transition-colors ${mode === "replace" ? "border-[#8C532B] bg-[#FBF6EE]" : "border-[#E5DEC9] bg-white"}`}>
                <p className="font-semibold text-sm text-[#1B3B2B]">Applica profenda completa</p>
                <p className="text-xs text-[#5F6B64] mt-0.5">Sostituisce l'intera profenda corrente dei cavalli selezionati.</p>
              </button>
            </div>

            <div className="max-w-[200px]"><Label>Valida dal</Label><Input type="date" className="mt-1.5" data-testid="bulk-valid-from" value={validFrom} onChange={(e) => { setValidFrom(e.target.value); invalidate(); }} /></div>

            {mode === "add_update" ? (
              <RationGroupsEditor groups={groups} onChange={patchGroups} activeSlots={activeSlots} activeItems={activeItems} itemsMap={itemsMap} catOrder={catOrder} />
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 flex items-start gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Questa operazione <strong>sostituisce</strong> la profenda corrente dei cavalli selezionati. Le versioni precedenti restano nello storico.</div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={replaceSource === "build" ? "default" : "outline"} data-testid="bulk-replace-build" onClick={() => { setReplaceSource("build"); invalidate(); }} className={replaceSource === "build" ? "bg-[#1B3B2B] hover:bg-[#142E1F]" : ""}>Costruisci</Button>
                  <Button type="button" size="sm" variant={replaceSource === "copy" ? "default" : "outline"} data-testid="bulk-replace-copy" onClick={() => { setReplaceSource("copy"); invalidate(); }} className={replaceSource === "copy" ? "bg-[#1B3B2B] hover:bg-[#142E1F]" : ""}>Copia da cavallo</Button>
                </div>
                {replaceSource === "build" ? (
                  <RationGroupsEditor groups={groups} onChange={patchGroups} activeSlots={activeSlots} activeItems={activeItems} itemsMap={itemsMap} catOrder={catOrder} />
                ) : (
                  <div className="space-y-2">
                    <div className="max-w-sm">
                      <Select value={copyFrom} onValueChange={loadCopySource}>
                        <SelectTrigger data-testid="bulk-copy-source"><SelectValue placeholder="Scegli cavallo sorgente…" /></SelectTrigger>
                        <SelectContent>{horses.map((h) => <SelectItem key={h.id} value={h.id}>{h.display_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {copyFrom && (copyRation === undefined ? <div className="py-3"><Loader2 className="h-5 w-5 animate-spin text-[#1B3B2B]" /></div>
                      : copyRation === null || !copyRation.lines?.length ? <p className="text-sm text-[#9AA39C]" data-testid="bulk-copy-empty">Questo cavallo non ha una profenda corrente.</p>
                      : (
                        <div className="rounded-lg border border-[#E5DEC9] bg-white p-3 space-y-1.5" data-testid="bulk-copy-preview">
                          {copyGroups.map((g, i) => (
                            <div key={i}><span className="font-mono-label text-[10px] uppercase text-[#8C532B]">{g.slot.name}</span>
                              <p className="text-sm text-[#3C463F] leading-snug">{g.lines.map((l) => `${itemsMap[l.feed_item_id]?.name || "?"} ${l.quantity === null || l.quantity === undefined ? "(nessun dato)" : fmtQty(l.quantity, itemsMap[l.feed_item_id]?.base_unit)}`).join(" · ")}</p>
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {preview && (
              <div className="rounded-lg border border-[#1B3B2B] bg-[#F7F3EC] p-3" data-testid="bulk-preview-counts">
                <p className="font-semibold text-sm text-[#1B3B2B] mb-1">Anteprima impatto · {preview.total} cavall{preview.total === 1 ? "o" : "i"}</p>
                {(preview.counts?.excluded || 0) > 0 && (
                  <div className="mb-2 rounded-md border border-[#DED6C2] bg-[#FBF6EE] p-2 text-xs text-[#8C6D4F] flex items-start gap-1.5" data-testid="bulk-excluded-banner">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{preview.counts.excluded} cavall{preview.counts.excluded === 1 ? "o" : "i"} escluso dall'operazione: fascia gestita dal proprietario.</span>
                  </div>
                )}
                {Object.values(preview.counts || {}).every((v) => !v) ? <p className="text-sm text-[#5F6B64]">Nessun impatto calcolato.</p>
                  : <ImpactSections data={preview} testidPrefix="bulk-preview" />}
                {preview.total - (preview.counts?.excluded || 0) <= 0 && (
                  <p className="mt-2 text-sm font-medium text-[#B4791F]" data-testid="bulk-all-excluded">Tutti i cavalli selezionati sono esclusi: nessuna operazione da applicare.</p>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" data-testid="bulk-cancel-btn" onClick={onClose}>Annulla</Button>
              <Button type="button" variant="outline" disabled={busy} data-testid="bulk-preview-btn" onClick={runPreview}>{busy && !preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Anteprima impatto</Button>
              <Button type="button" disabled={busy || !preview || (preview.total - (preview.counts?.excluded || 0) <= 0)} data-testid="bulk-apply-btn" onClick={runApply} className="bg-[#1B3B2B] hover:bg-[#142E1F]">{busy && preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Applica a {preview ? preview.total - (preview.counts?.excluded || 0) : n} cavall{(preview ? preview.total - (preview.counts?.excluded || 0) : n) === 1 ? "o" : "i"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
